import React, { useEffect } from "react";
import Button from "../../../../components/Button/Button";
import Typography from "../../../../components/Typography/Typography";
import "./AIAssitPage.scss";
import Loading from "../../../../components/LoadingSkeleton/Loading";

function SubmitAsset({
    submissionResults,
    extractedCount,
    onBack,
    onRetry,
    isSubmitting = false,
}) {
    useEffect(() => {
        if (submissionResults && submissionResults.errorCount === 0) {
            const timer = setTimeout(onBack, 3000);
            return () => clearTimeout(timer);
        }
    }, [submissionResults, onBack]);

    const hasResults = Boolean(submissionResults);
    const allSuccess = hasResults && submissionResults.errorCount === 0;
    const partialSuccess =
        hasResults &&
        submissionResults.successCount > 0 &&
        submissionResults.errorCount > 0;

    const view = (() => {
        if (isSubmitting && !hasResults) {
            return {
                icon: "bi bi-arrow-repeat",
                color: "text-arrow",
                title: "Creating Assets...",
                lines: [
                    `Submitting ${extractedCount} assets to your batch records.`,
                    "Please wait while we process your assets.",
                ],
                buttons: null,
            };
        }

        if (!hasResults) {
            return {
                icon: "bi bi-check-circle",
                color: "text-success",
                title: "Assets Successfully Submitted!",
                lines: [
                    `Your ${extractedCount} assets have been added to the batch records.`,
                    "Redirecting to Batch Records in a few seconds...",
                ],
                buttons: null,
            };
        }

        if (allSuccess) {
            return {
                icon: "bi bi-check-circle",
                color: "text-success",
                title: "All Assets Successfully Submitted!",
                lines: [
                    `Your ${submissionResults.successCount} assets have been added to the batch records.`,
                    "Redirecting to Batch Records in a few seconds...",
                ],
                buttons: null,
            };
        }

        if (partialSuccess) {
            return {
                icon: "bi bi-exclamation-triangle",
                color: "text-warning",
                title: "Partial Success",
                lines: [
                    `Successfully created ${submissionResults.successCount} assets!`,
                    `Failed to create ${submissionResults.errorCount} assets.`,
                    "Failed assets remain in the list for retry.",
                ],
                buttons: (
                    <div className="d-flex justify-content-center mt-3">
                        <Button
                            onClick={onRetry}
                            variant="primary"
                            fullWidth={false}
                            className="me-2"
                        >
                            Retry Failed Assets
                        </Button>
                        <Button
                            onClick={onBack}
                            variant="secondary"
                            fullWidth={false}
                        >
                            Go to Batch Records
                        </Button>
                    </div>
                ),
            };
        }

        return {
            icon: "bi bi-x-circle",
            color: "text-danger",
            title: "Asset Submission Failed",
            lines: [
                `Unable to create ${submissionResults.errorCount} ${submissionResults.errorCount === 1 ? "asset" : "assets"}.`,
                "Something went wrong during the submission process.",
            ],
            buttons: (
                <div className="d-flex justify-content-center mt-3">
                    <Button
                        onClick={onRetry}
                        variant="primary"
                        fullWidth={false}
                        className="me-2"
                    >
                        Retry All Assets
                    </Button>
                    <Button
                        onClick={onBack}
                        variant="secondary"
                        fullWidth={false}
                    >
                        Go to Batch Records
                    </Button>
                </div>
            ),
        };
    })();

    return (
        <div className="text-center">
            <div className="mb-4">
                <div className="spinner-wrapper mx-auto d-flex justify-content-center">
                    {isSubmitting && !hasResults ? (
                        <Loading />
                    ) : (
                        <i
                            className={`${view.icon} ${view.color}`}
                            style={{ fontSize: "4rem" }}
                        ></i>
                    )}
                </div>
            </div>
            <Typography variant="h5" className="mb-3">
                {view.title}
            </Typography>
            {view.lines.map((text, idx) => (
                <Typography
                    variant="body1"
                    key={idx}
                    className={`mb-2 ${idx === 0 ? "text-muted" : "text-muted small"}`}
                >
                    {text}
                </Typography>
            ))}
            {view.buttons}
        </div>
    );
}

export default SubmitAsset;
